import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQclansAccessToken, hasStoredSession } from '@/lib/logto';
import type {
  CapitalSeasonsResponse,
  ClanMembersResponse,
  ClanSearchResponse,
  CoachArmyRequest,
  CoachResponse,
  CoachUpgradePriorityRequest,
  CoachWarAttackRequest,
  CocClan,
  CocLinkStatus,
  CocPlayer,
  Competition,
  CompetitionsResponse,
  CurrentWar,
  CwlGroup,
  LeaderboardQuery,
  LeaderboardResponse,
  VerifyCocLinkRequest,
  VerifyCocLinkResponse,
} from '@/types/qclans';

/**
 * QClans API client. Talks to qclans-api at `/v1/*` — the QLASH Clash of Clans
 * vertical (stats / war + CWL + capital tracking / competitions / AI coaching).
 *
 * Auth: Logto Bearer JWT with audience `https://qclans.qlash.gg/api`, minted by
 * getQclansAccessToken() through the shared serialized refresh-token redemption
 * (same pattern as qriots/qbot/qsocials/qduels/qchess). qclans-api validates the
 * JWT `aud` against exactly that audience and the issuer against
 * `https://account.qlash.gg/oidc`.
 *
 * ⚠️ Logto app grant required (P6/deploy blocker): minting a token for the
 * qclans audience only works once the "Qlash API QClans" API resource is
 * REGISTERED on the Logto tenant AND granted to the TMA's Logto application
 * (Logto console → Applications → <TMA app> → API resources). Until then
 * getQclansAccessToken() resolves to null (refresh grant → HTTP 400), no
 * Authorization header is attached, and qclans answers 401.
 *
 * Dev-bypass escape hatch: qclans-api accepts `X-Dev-Bypass: <token>` (+ optional
 * `X-Dev-User`) only when it runs NODE_ENV=development. When VITE_QCLANS_DEV_BYPASS
 * is set (dev builds only), this client attaches those headers and SKIPS the Logto
 * Bearer path — so the `/coc` surface is testable against the MacMini before the
 * Logto resource exists. The value is gated to dev in env.ts; never set in prod.
 *
 * Dev: env.qclansApiUrl = '/qclans-api', Vite proxy forwards to the MacMini
 * (`http://100.94.221.52:3700`).
 * Prod: env.qclansApiUrl = 'https://qclans.qlash.gg'.
 *
 * Like qchess/qduels, responses are NOT wrapped in a `{ data }` / `{ success }`
 * envelope, and errors return `{ error: "<code>" }`.
 *
 * Tag invalidation:
 *   - 'Link' refetches `GET /v1/link` after verify/unlink.
 */
export const qclansApi = createApi({
  reducerPath: 'qclansApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qclansApiUrl,
      prepareHeaders: async (headers) => {
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        // Dev-bypass ONLY when there is no Logto session — the anonymous-dev
        // case. It used to take precedence outright, which meant a signed-in
        // user in a dev build never even attempted their own token and silently
        // became the shared dev identity. That masks exactly the failures this
        // hardening exists to surface (dead grant, resource not granted), so a
        // signed-in user now falls through to the real token and, if the mint
        // fails, gets a 401 that says so. Same guard as qgammon/ and qcp/auth.ts.
        if (env.qclansDevBypass && !hasStoredSession()) {
          headers.set('X-Dev-Bypass', env.qclansDevBypass);
          if (env.qclansDevUser) headers.set('X-Dev-User', env.qclansDevUser);
          return headers;
        }
        const token = await getQclansAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        return headers;
      },
    }),
    {
      // Same retry policy as qchess/qduels: only retry transient network/5xx
      // ONCE. 4xx (war_log_private, not_in_cwl, rate_limited, ai_not_configured,
      // 401) are deterministic — a retry just delays the error.
      retryCondition: (error, _args, { attempt }) => {
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  ),
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Link'],
  endpoints: (builder) => ({
    /* ── Linking (verifytoken) ──────────────────────────────────────── */

    /**
     * The caller's linked CoC accounts (`GET /v1/link`). `linked: false` (empty
     * `accounts`) is the "not linked yet" state returned with HTTP 200 — NOT an
     * error — so screens branch on `data.linked` / `data.accounts.length`.
     */
    getCocLink: builder.query<CocLinkStatus, void>({
      query: () => '/v1/link',
      providesTags: ['Link'],
      keepUnusedDataFor: 60,
    }),

    /**
     * Verify ownership of a CoC account with the in-game one-time API token
     * (`POST /v1/link/coc/verify`). 400 `invalid_token` when the token is wrong
     * or expired. Refreshes the link list so the new account shows immediately.
     */
    verifyCocLink: builder.mutation<VerifyCocLinkResponse, VerifyCocLinkRequest>({
      query: (body) => ({ url: '/v1/link/coc/verify', method: 'POST', body }),
      invalidatesTags: ['Link'],
    }),

    /** Unlink a CoC account (`DELETE /v1/link/coc?playerTag=` → 204). */
    unlinkCoc: builder.mutation<void, { playerTag: string }>({
      query: ({ playerTag }) => ({
        url: '/v1/link/coc',
        method: 'DELETE',
        params: { playerTag },
      }),
      invalidatesTags: ['Link'],
    }),

    /* ── Player stats ───────────────────────────────────────────────── */

    /** A CoC player (`GET /v1/players/:tag`). 404 `player_not_found`. */
    getPlayer: builder.query<CocPlayer, string>({
      query: (tag) => `/v1/players/${encodeURIComponent(tag)}`,
      keepUnusedDataFor: 600,
    }),

    /* ── Clan stats ─────────────────────────────────────────────────── */

    /**
     * Search clans (`GET /v1/clans/search`). Requires at least one filter
     * (name / locationId / labelIds / warFrequency); we surface name search.
     */
    searchClans: builder.query<ClanSearchResponse, { name: string; limit?: number }>({
      query: ({ name, limit = 20 }) => ({
        url: '/v1/clans/search',
        params: { name, limit },
      }),
    }),

    /** A CoC clan (`GET /v1/clans/:tag`). */
    getClan: builder.query<CocClan, string>({
      query: (tag) => `/v1/clans/${encodeURIComponent(tag)}`,
      keepUnusedDataFor: 600,
    }),

    /** A clan's member list (`GET /v1/clans/:tag/members`). */
    getClanMembers: builder.query<ClanMembersResponse, string>({
      query: (tag) => `/v1/clans/${encodeURIComponent(tag)}/members`,
      keepUnusedDataFor: 600,
    }),

    /* ── War / CWL / Capital (live, cached) ─────────────────────────── */

    /**
     * The clan's current war (`GET /v1/clans/:tag/currentwar`). 403
     * `war_log_private` when the clan hasn't set its war log public — the
     * consuming screen renders the "make war log public" hint instead of data.
     */
    getCurrentWar: builder.query<CurrentWar, string>({
      query: (tag) => `/v1/clans/${encodeURIComponent(tag)}/currentwar`,
    }),

    /**
     * The clan's CWL league group (`GET /v1/clans/:tag/cwl`). 404 `not_in_cwl`
     * off-season — the screen renders the "not in a CWL season" state.
     */
    getCwl: builder.query<CwlGroup, string>({
      query: (tag) => `/v1/clans/${encodeURIComponent(tag)}/cwl`,
    }),

    /** The clan's Clan Capital raid seasons (`GET /v1/clans/:tag/capital`). */
    getCapital: builder.query<CapitalSeasonsResponse, string>({
      query: (tag) => `/v1/clans/${encodeURIComponent(tag)}/capital`,
      keepUnusedDataFor: 600,
    }),

    /* ── Competitions & leaderboard ─────────────────────────────────── */

    /** QLASH CoC competitions (`GET /v1/competitions`). */
    listCompetitions: builder.query<CompetitionsResponse, void>({
      query: () => '/v1/competitions',
      keepUnusedDataFor: 300,
    }),

    /** A single competition (`GET /v1/competitions/:id`). */
    getCompetition: builder.query<Competition, string>({
      query: (id) => `/v1/competitions/${encodeURIComponent(id)}`,
    }),

    /** A competition's standings (`GET /v1/competitions/:id/leaderboard`). */
    getCompetitionLeaderboard: builder.query<
      LeaderboardResponse,
      { id: string } & LeaderboardQuery
    >({
      query: ({ id, page, limit }) => ({
        url: `/v1/competitions/${encodeURIComponent(id)}/leaderboard`,
        params: {
          ...(page != null ? { page } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: 120,
    }),

    /* ── AI coaching (rate-limited per user) ────────────────────────── */
    //
    // 429 `rate_limited` (with Retry-After) and 503 `ai_not_configured` are the
    // two states the Coach screen handles explicitly. On 503 the coach is
    // hidden/disabled — the API has no Anthropic key.

    coachUpgradePriority: builder.mutation<CoachResponse, CoachUpgradePriorityRequest>({
      query: (body) => ({ url: '/v1/coach/upgrade-priority', method: 'POST', body }),
    }),

    coachArmy: builder.mutation<CoachResponse, CoachArmyRequest>({
      query: (body) => ({ url: '/v1/coach/army', method: 'POST', body }),
    }),

    coachWarAttack: builder.mutation<CoachResponse, CoachWarAttackRequest>({
      query: (body) => ({ url: '/v1/coach/war-attack', method: 'POST', body }),
    }),
  }),
});

export const {
  useGetCocLinkQuery,
  useVerifyCocLinkMutation,
  useUnlinkCocMutation,
  useGetPlayerQuery,
  useLazySearchClansQuery,
  useGetClanQuery,
  useGetClanMembersQuery,
  useGetCurrentWarQuery,
  useGetCwlQuery,
  useGetCapitalQuery,
  useListCompetitionsQuery,
  useGetCompetitionQuery,
  useGetCompetitionLeaderboardQuery,
  useCoachUpgradePriorityMutation,
  useCoachArmyMutation,
  useCoachWarAttackMutation,
} = qclansApi;
